import $$api from 'models/api';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { Col, Form, Spinner } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

import HookFormControl from 'components/HookFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import PopUpWrapper from 'components/StyledTemplates/PopUpWrapper';
import { Button } from 'components/StyledTemplates/StyledTemplates';

import AlertsService from 'services/AlertsService';
import ModalsService from 'services/ModalsService';
import RemainingStepsService from 'services/RemainingStepsService';

import useModal from 'hooks/useModal';
import useResources from 'hooks/useResources';

import { AddressWithNameZod } from 'types/zod/address-with-name';

const ShippingDetailsCollectModal = () => {
    const form = useForm();
    const { formState } = form;
    const { isSubmitting } = formState;

    const { t } = useResources();

    const { close } = useModal('SHIPPING_DETAILS_COLLECT', {
        onOpen: () => {
            const me = $$auth.$currentParticipant.getState();
            const myAddress = me?.address?.mailing || me?.address?.domicile || {};
            form.setValue('name', me?.full_name || '');
            form.setValue('address', myAddress);
        },
    });

    const useSuggestedAddress = (value: string, item: any) => {
        if (value) {
            form.setValue('address.address_line1', item.street_line);
            form.setValue('address.address_line2', item.secondary);
            form.setValue('address.city', item.city);
            form.setValue('address.state_code', item.state);
            form.setValue('address.zip', item.zipcode);
        }
    };

    const handlers = {
        onCloseClick: () => {
            close();
        },
        onSubmit: async (formValues: unknown) => {
            const estatePlan = $$ep.stores.$epQInstance.getState();
            if (!estatePlan) {
                return;
            }
            const address = AddressWithNameZod.parse(formValues);
            await $$api.qInstances.shipDocsFx({ id: estatePlan.questionnaire_instance_id, address });
            AlertsService.showSuccess(t('shipping.addressCollectSuccess'));
            await Promise.all([RemainingStepsService.completeStep('printed'), $$api.participants.getMeFx()]);
            ModalsService.hideModal('VIEW_DOCUMENTS_MODAL');
            close();
        },
    };

    return (
        <PopUpWrapper name="SHIPPING_DETAILS_COLLECT" size="lg" whiteCross={true}>
            <CommunityTab
                title="Shipping address"
                extraBody="> * { width: 100%; }; gap: 15px;"
                children={
                    <>
                        <FormProvider {...form}>
                            <Form onSubmit={form.handleSubmit(handlers.onSubmit)}>
                                <Form.Row>
                                    <Form.Group as={Col} controlId="name">
                                        <HookFormControl.Text
                                            name="name"
                                            label={t(`shipping.name`)}
                                            maxLength={256}
                                            rules={{ required: t(`shipping.name_error`) }}
                                        />
                                    </Form.Group>
                                </Form.Row>
                                <Form.Row>
                                    <Form.Group as={Col} sm={6} controlId="address.address_line1">
                                        <HookFormControl.AddressAutocomplete
                                            name="address.address_line1"
                                            label={t(`shipping.address_line1`)}
                                            rules={{ required: t(`shipping.address_line1_error`) }}
                                            onSelect={useSuggestedAddress}
                                            state={form.getValues('address.state_code')}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} sm={6} controlId="address.address_line2">
                                        <HookFormControl.Text
                                            name="address.address_line2"
                                            label={t(`shipping.address_line2`)}
                                            maxLength={60}
                                        />
                                    </Form.Group>
                                </Form.Row>
                                <Form.Row>
                                    <Form.Group as={Col} sm={4} controlId="address.city">
                                        <HookFormControl.Text
                                            name="address.city"
                                            label={t(`shipping.city`)}
                                            rules={{ required: t(`shipping.city_error`) }}
                                            maxLength={60}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} sm={4} controlId="address.state_code">
                                        <HookFormControl.State
                                            name="address.state_code"
                                            label={t(`shipping.state`)}
                                            rules={{ required: t(`shipping.state_error`) }}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} sm={4} controlId="address.zip">
                                        <HookFormControl.Text
                                            name="address.zip"
                                            label={t(`shipping.zip`)}
                                            pattern="(^\d{5}$)|(^\d{5}-\d{4}$)"
                                            type="text"
                                            maxLength={10}
                                            className="zip"
                                            rules={{ required: t(`shipping.zip_error`) }}
                                        />
                                    </Form.Group>
                                </Form.Row>
                                <div className="text-right mt-3">
                                    <Button
                                        data-qaid="submit_button"
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-5"
                                    >
                                        {t('shippingDetailsCollectModal.submit')}{' '}
                                        {isSubmitting && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </Button>
                                    <Button data-qaid="cancel_button" variant="plain" onClick={handlers.onCloseClick}>
                                        {t('shippingDetailsCollectModal.cancel')}
                                    </Button>
                                </div>
                            </Form>
                        </FormProvider>
                    </>
                }
            />
        </PopUpWrapper>
    );
};

export default ShippingDetailsCollectModal;
